import { useTranslation } from "react-i18next";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  Link,
} from "react-router-dom";
import WorkspaceLayout from "../layouts/WorkspaceLayout";
import AuthenticationPage from "../pages/authentication";
import DashboardPage from "../pages/dashboard";
import ProfilePage from "../pages/profile";
import { useAuth } from "../store/AuthContext";
import { Loading, ErrorNotice, Empty } from "../components/common/Primitives";
import { features } from "../config/constants/features";
import Visits from "../pages/visits";
import Correspondence from "../pages/correspondence";
import Interviews from "../pages/interviews";
import Employees from "../pages/employees";
import Departments from "../pages/departments";
import RequiredPasswordPage from "../pages/authentication/RequiredPasswordPage";
import MeetingReservations from "../pages/meeting-reservations";
import MeetingRooms from "../pages/meeting-rooms";
import Floors from "../pages/floors";
import Roles from "../pages/roles";
import MyActivityPage from "../pages/my-activity";
const pages = [
  Visits,
  Correspondence,
  Interviews,
  MeetingReservations,
  Employees,
  Departments,
  Floors,
  Roles,
  MeetingRooms,
];
function Guard() {
  const { t } = useTranslation();
  const { user, loading, error, refresh } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (error)
    return (
      <div className="connection-error">
        <ErrorNotice
          message={
            t("The workspace could not connect to its server.") + " " + t(error)
          }
          retry={refresh}
        />
      </div>
    );
  if (!user) return <Navigate to="/login" state={{ oldPath: location.pathname + location.search }} replace />;
  if (user.mustChangePassword && location.pathname !== "/change-password")
    return <Navigate to="/change-password" replace />;
  const feature = features.find((f) =>
    location.pathname.startsWith("/" + f.key),
  );
  if (
    feature?.employeeVisible &&
    feature.key !== "meeting-reservations" &&
    user.role !== "Admin"
  )
    return <Navigate to="/my-activity" replace />;
  if (feature && !feature.employeeVisible && !feature.roles.includes(user.role))
    return <Navigate to="/" replace />;
  return <Outlet />;
}
export const router = createBrowserRouter([
  {
    element: <Guard />,
    children: [
      { path: "change-password", element: <RequiredPasswordPage /> },
      {
        element: <WorkspaceLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: "my-activity", element: <MyActivityPage /> },
          ...features.map((f, i) => {
            const Page = pages[i];
            return { path: f.key, element: <Page /> };
          }),
          { path: "profile", element: <ProfilePage /> },
        ],
      },
    ],
  },
  ...[
    "login",
    "forgot-password",
    "reset-password",
    "verify-email",
  ].map((path) => ({ path, element: <AuthenticationPage key={path} /> })),
  {
    path: "*",
    element: <NotFound />,
  },
]);

function NotFound() {
  const { t } = useTranslation();
  return (
    <Empty title={t("This page couldn’t be found")}>
      <Link to="/">{t("Return to your workspace")}</Link>
    </Empty>
  );
}
